import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import ApiService from "../../../services/api-service";
import { getBalance } from "../../../asyncAction/asyncBalance";
import { 
  fullName, 
  getErrorString, 
  putRecors,
  getUserDetail 
} from "../../../constants/function";
import notifier from "../../../components/Notifier";

/**
 * Simplified Artist Profile Hook - NO CACHING
 * - Direct API calls for reliability
 * - Simple state management
 * - Better error handling
 */
export default function useArtistProfileSimple(id, referralId) {
  // State management
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [userData, setUserData] = useState({});
  const [music, setMusic] = useState([]);
  const [nftMusic, setNftMusic] = useState([]);
  const [playlistMusic, setPlaylistMusic] = useState([]);
  const [playlistNft, setPlaylistNft] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [videos, setVideos] = useState([]);
  const [trendingMusic, setTrendingMusic] = useState({});
  const [topMusic, setTopMusic] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [count, setCount] = useState(0);
  const [active, setActive] = useState(0);
  const [error, setError] = useState(null);
  
  const dispatch = useDispatch();
  const userDetail = getUserDetail();

  // Load initial data - no caching, just direct API calls
  const loadInitialData = useCallback(async (artistId) => {
    if (!artistId || artistId === 'undefined' || artistId === 'null') {
      console.error('Invalid artist ID provided:', artistId);
      setError('Invalid artist profile');
      setInitialLoading(false);
      return;
    }

    console.log(`🚀 Loading data for artist: ${artistId}`);
    setInitialLoading(true);
    setError(null);

    try {
      // Load all initial data in parallel with a reasonable timeout
      const apiTimeout = 10000; // 10 second timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('API timeout')), apiTimeout)
      );
      
      const [userResponse, trendingResponse, musicResponse] = await Promise.race([
        Promise.allSettled([
          ApiService.USER_DETAILS(`${artistId}`),
          ApiService.TRENDING(`${artistId}`),
          ApiService.TOP_ALBUM_MUSIC_STUDIO_LIST({
            id: artistId,
            type: "music",
            pageNo: 1,
            pageSize: 14,
          })
        ]),
        timeoutPromise
      ]);

      // Handle user data
      if (userResponse.status === 'fulfilled' && userResponse.value?.result) {
        const userData = userResponse.value.result;
        console.log(`✅ Successfully loaded user data:`, {
          id: userData._id,
          name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
        });
        
        // Process top5Music subscription status
        if (userData?.top5Music?.length > 0) {
          userData.top5Music.forEach((m) => {
            if (m.uploader) {
              m.uploader.isSubscribed = userData.isSubscribed;
            }
          });
        }
        
        setUserData(userData);

        // Track analytics
        if (window.location.href.includes("referralId=")) {
          putRecors({
            id: userData._id,
            referralLink: window.location.href,
            eventType: ["sharedInbound"],
            eventAsset: userData._id,
            eventValue: ["Profile"],
            socialMedia: null,
            referralId: referralId,
          });
        } else {
          putRecors({
            id: userData._id,
            referralLink: window.location.href,
            eventType: ["view"],
            eventAsset: userData._id,
            eventValue: ["Profile"],
            socialMedia: null,
          });
        }
      } else {
        console.error('❌ Failed to load user data:', userResponse.reason);
        setError('Failed to load artist profile');
        setUserData({ _id: artistId, error: true });
      }

      // Handle trending music (don't fail if this errors)
      if (trendingResponse.status === 'fulfilled') {
        setTrendingMusic(trendingResponse.value.result || {});
      } else {
        console.log('⚠️ Failed to load trending music, using empty data');
        setTrendingMusic({});
      }

      // Handle initial music
      if (musicResponse.status === 'fulfilled') {
        const musicData = musicResponse.value.result || [];
        setMusic(musicData);
        setLoaded(musicData.length < 14);
      } else if (musicResponse.reason?.response?.data?.message === "Artist has no music") {
        setMusic([]);
        setLoaded(true);
      } else {
        console.log('⚠️ Failed to load music data');
        setMusic([]);
        setLoaded(true);
      }

    } catch (error) {
      console.error('❌ Error loading artist profile:', error);
      setError('Failed to load artist profile');
      
      // Handle specific error cases
      if (error.response?.status === 404 || 
          (error.response?.status === 400 && error.response?.data?.message === 'User not found')) {
        notifier.error('Artist profile not found');
      } else {
        notifier.error('Failed to load artist profile');
      }
    } finally {
      setInitialLoading(false);
    }
  }, [referralId]);

  // Get music with better error handling
  const getMyMusic = async (_id, pageNo) => {
    if (pageNo === 1 && music.length === 0) {
      setLoading(true);
    }
    
    try {
      const response = await ApiService.TOP_ALBUM_MUSIC_STUDIO_LIST({
        id: _id,
        type: "music",
        pageNo: pageNo,
        pageSize: 14,
      });
      
      const newMusic = response.result || [];
      setLoaded(newMusic.length < 14);
      
      if (pageNo === 1) {
        setMusic(newMusic);
      } else {
        setMusic(prevMusic => [...prevMusic, ...newMusic]);
      }
      
      return newMusic;
    } catch (error) {
      if (error.response?.data?.message === "Artist has no music") {
        setLoaded(true);
        if (pageNo === 1) setMusic([]);
      } else {
        console.error('Error loading music:', error);
      }
      return [];
    } finally {
      if (pageNo === 1) {
        setLoading(false);
      }
    }
  };

  // Get playlists
  const getPlayLists = async (_id) => {
    setLoading(true);
    try {
      const response = await ApiService.TOP_ALBUM_MUSIC_STUDIO_LIST({
        id: _id,
        type: "studio",
        pageNo: 1,
        pageSize: 16,
      });
      
      setPlaylistMusic(response.result || []);
      return response.result || [];
    } catch (error) {
      if (error.response?.data?.message !== "Artist has no music") {
        console.error('Error loading playlists:', error);
      }
      setPlaylistMusic([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Get NFTs with pagination
  const getNfts = async (_id, pageNo) => {
    if (pageNo === 1) {
      setLoading(true);
    }
    
    try {
      const response = await ApiService.TOP_ALBUM_MUSIC_STUDIO_LIST({
        id: _id,
        type: "nft",
        pageNo: pageNo,
        pageSize: 16,
      });
      
      const newNfts = response.result || [];
      setLoaded(newNfts.length < 16);
      
      if (pageNo === 1) {
        setNftMusic(newNfts);
      } else {
        setNftMusic(prev => [...prev, ...newNfts]);
      }
      
      return newNfts || [];
    } catch (error) {
      if (error.response?.data?.message !== "Artist has no NFT") {
        console.error('Error loading NFTs:', error);
      }
      setLoaded(true);
      return [];
    } finally {
      if (pageNo === 1) {
        setLoading(false);
      }
    }
  };

  // Get albums
  const getAlbum = async (_id, pageNo) => {
    try {
      setLoading(true);
      const response = await ApiService.TOP_ALBUM_MUSIC_STUDIO_LIST({
        id: _id,
        type: "album",
        pageNo: pageNo,
        pageSize: 16,
      });
      
      const newAlbums = response.result || [];
      setLoaded(newAlbums.length < 16);
      
      if (pageNo === 1) {
        setAlbums(newAlbums);
      } else {
        setAlbums(prev => [...prev, ...newAlbums]);
      }
      
      return newAlbums;
    } catch (error) {
      console.error('Error loading albums:', error);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Get videos
  const getVideos = async (id, pageNo) => {
    try {
      setLoading(true);
      const response = await ApiService.GET_VIDEOS(
        `?id=${id}&pageNo=${pageNo}&pageSize=16`
      );
      
      const newVideos = response.result || [];
      setLoaded(newVideos.length < 16);
      
      if (pageNo === 1) {
        setVideos(newVideos);
      } else {
        setVideos(prev => [...prev, ...newVideos]);
      }
      
      return newVideos;
    } catch (error) {
      console.error('Error loading videos:', error);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab switch
  const handleTabSwitch = (e, tab) => {
    if (active === tab) return;
    
    setActive(tab);
    setPageNo(1);
    setLoaded(false);
    
    switch (tab) {
      case 0:
        // Popular tab - load trending/popular music
        getMyMusic(userData._id, 1);
        break;
      case 1:
        // Music tab - this is handled by internal sub-tabs in ArtistContent
        // No need to load data here as it's handled by the Music tab component
        break;
      case 2:
        // Playlists tab
        getPlayLists(userData._id);
        break;
      default:
        break;
    }
  };

  // Handle scroll for pagination
  const handleScroll = async (target) => {
    const scrollPercentage = (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;

    if (scrollPercentage > 80 && !loaded && !loading) {
      const nextPage = pageNo + 1;
      setPageNo(nextPage);
      
      switch (active) {
        case 0:
          getMyMusic(userData._id, nextPage);
          break;
        case 1:
          // Music tab handled internally
          break;
        case 2:
          // Playlists don't paginate
          break;
        default:
          break;
      }
    }
  };

  // Clear state and load data when user ID changes
  useEffect(() => {
    if (id && id !== 'undefined' && id !== 'null') {
      console.log(`🔄 User ID changed to: ${id}`);
      
      // Clear all state
      setUserData({});
      setMusic([]);
      setNftMusic([]);
      setPlaylistMusic([]);
      setAlbums([]);
      setVideos([]);
      setTrendingMusic({});
      setPageNo(1);
      setLoaded(false);
      setActive(0);
      setError(null);
      setLoading(false);
      
      // Load data for new user
      loadInitialData(id);
    }
  }, [id, loadInitialData]);

  return {
    // State
    loading,
    initialLoading,
    userData,
    music,
    nftMusic,
    playlistMusic,
    playlistNft,
    albums,
    videos,
    trendingMusic,
    topMusic,
    pageNo,
    loaded,
    count,
    active,
    error,
    
    // Methods
    loadInitialData,
    getMyMusic,
    getPlayLists,
    getNfts,
    getAlbum,
    getVideos,
    handleTabSwitch,
    handleScroll,
    
    // Setters
    setUserData,
    setMusic,
    setNftMusic,
    setPlaylistMusic,
    setPlaylistNft,
    setAlbums,
    setVideos,
    setTrendingMusic,
    setTopMusic,
    setPageNo,
    setLoaded,
    setCount,
    setActive,
    setLoading,
  };
}